/**
 * Plain names for the things people are charged for.
 *
 * The API speaks in flows and kinds — `video_generation` / `shot`,
 * `ideation_improve` — because that is how the billing rows are keyed. Nobody
 * being shown a bill thinks in those words, so this is the one place they are
 * translated, shared by the billing page and the running total inside a
 * project so the same charge never gets two different names.
 *
 * A flow with no entry here falls back to its own name with the underscores
 * taken out. That is deliberately survivable rather than clever: a billable
 * flow added later shows up as slightly awkward prose instead of vanishing
 * from a total that still includes its money.
 */

const CATEGORY_VIDEO = 'Video generation';
const CATEGORY_ANALYSIS = 'Analysis';
const CATEGORY_EDITING = 'Editing';
const CATEGORY_IDEATION = 'Ideation';

// Which broad category a flow belongs to, for the by-category view.
const CATEGORY_BY_FLOW = {
    video_generation: CATEGORY_VIDEO,
    video_analysis: CATEGORY_ANALYSIS,
    idea_analysis: CATEGORY_ANALYSIS,
    video_edit: CATEGORY_EDITING,
    workspace_review: CATEGORY_EDITING,
    ideation_improve: CATEGORY_IDEATION,
    ideation_chat: CATEGORY_IDEATION,
};

export const CATEGORY_ORDER = [
    CATEGORY_VIDEO,
    CATEGORY_ANALYSIS,
    CATEGORY_EDITING,
    CATEGORY_IDEATION,
];

// One accent per category. Deliberately equal in lightness so they read as
// peers rather than as severity — none of these is a warning.
export const CATEGORY_COLORS = {
    [CATEGORY_VIDEO]: 'var(--ui-accent)',
    [CATEGORY_ANALYSIS]: 'var(--ui-warning)',
    [CATEGORY_EDITING]: '#2f6f7d',
    [CATEGORY_IDEATION]: '#6b5ca5',
};

// What one charge of this flow/kind IS, as a noun a person would use.
// `one` is used when the count is 1, `many` otherwise.
const ITEM_NAMES = {
    'video_generation|shot': { one: 'shot rendered', many: 'shots rendered' },
    'video_generation|portrait': { one: 'character portrait', many: 'character portraits' },
    'video_generation|breakdown': { one: 'script breakdown', many: 'script breakdowns' },
    'video_generation|stitch': { one: 'final cut stitched', many: 'final cuts stitched' },
    'video_generation|music': { one: 'music track', many: 'music tracks' },
    'video_generation|': { one: 'render', many: 'renders' },
    'video_analysis|': { one: 'video analysis', many: 'video analyses' },
    'idea_analysis|': { one: 'idea analysis', many: 'idea analyses' },
    'video_edit|': { one: 'AI edit pass', many: 'AI edit passes' },
    'workspace_review|': { one: 'cut review', many: 'cut reviews' },
    'ideation_improve|': { one: 'idea rewrite', many: 'idea rewrites' },
    'ideation_chat|': { one: 'ideation chat', many: 'ideation chats' },
};

function humanise(flow) {
    return String(flow || 'other').replace(/_/g, ' ');
}

export function categoryFor(flow) {
    return CATEGORY_BY_FLOW[flow] || humanise(flow);
}

/** "87 shots rendered" — the count is the point, not the ids behind it. */
export function itemLabel(flow, kind, count) {
    const names = ITEM_NAMES[`${flow}|${kind || ''}`];
    const n = Number(count || 0);
    if (!names) {
        return `${n} × ${humanise(flow)}${kind ? ` (${kind})` : ''}`;
    }
    return `${n} ${n === 1 ? names.one : names.many}`;
}

/** "avg $1.24 each" — omitted for a single charge, where it says nothing. */
export function eachLabel(count, costUsd, formatPrice) {
    const n = Number(count || 0);
    if (n <= 1) return '';
    return `avg ${formatPrice(Number(costUsd || 0) / n)} each`;
}

export function shareLabel(costUsd, totalUsd) {
    const total = Number(totalUsd || 0);
    if (total <= 0) return '';
    return `${((Number(costUsd || 0) / total) * 100).toFixed(1)}%`;
}

/**
 * Roll `cost_groups` (one row per flow+kind) up into categories, keeping the
 * flow/kind rows as the detail inside each.
 */
export function toCategoryGroups(costGroups) {
    const buckets = new Map();

    (costGroups || []).forEach((row) => {
        const category = categoryFor(row.entity_flow);
        if (!buckets.has(category)) {
            buckets.set(category, { id: category, label: category, cost: 0, count: 0, rows: [] });
        }
        const bucket = buckets.get(category);
        bucket.cost += Number(row.cost_usd || 0);
        bucket.count += Number(row.event_count || 0);
        bucket.rows.push(row);
    });

    return [...buckets.values()]
        .map((bucket) => ({
            ...bucket,
            color: CATEGORY_COLORS[bucket.label] || 'var(--ui-border-strong)',
            rows: [...bucket.rows].sort((a, b) => Number(b.cost_usd) - Number(a.cost_usd)),
        }))
        .sort((a, b) => b.cost - a.cost);
}

/** `workflow_groups` in display order, with the unattributed residue last. */
export function toProjectGroups(workflowGroups) {
    return (workflowGroups || [])
        .map((group, index) => ({
            id: group.workflow_id || 'unattributed',
            label: group.name || (group.workflow_id ? 'Untitled project' : 'Not in a project'),
            // A residue is not a project, so it gets the neutral swatch rather
            // than a place in the category palette.
            color: group.workflow_id
                ? Object.values(CATEGORY_COLORS)[index % CATEGORY_ORDER.length]
                : 'var(--ui-border-strong)',
            cost: Number(group.cost_usd || 0),
            count: Number(group.event_count || 0),
            attributed: !!group.workflow_id,
            rows: group.categories || [],
        }));
}
